import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Button, Heading, Text } from '../atomic';
import GreenCornerCard from '../ui/GreenCornerCard';

/**
 * Engagement models section showing different collaboration approaches
 *
 * @returns {JSX.Element} Engagement models section component
 */
function EngagementModelsSection() {
  const collaborationModels = [
    {
      title: 'Strategic Consulting',
      description:
        'Leverage our expertise to guide your healthcare transformation journey',
      features: [
        'Executive advisory services',
        'Digital transformation roadmaps',
        'Technology assessment and selection',
        'Change management support',
        'Investment options',
      ],
    },
    {
      title: 'Implementation Services',
      description:
        'End-to-end implementation of healthcare technology solutions',
      features: [
        'EMR/EHR implementations',
        'AI/ML integration',
        'Workflow automation',
        'System integration',
      ],
    },
    {
      title: 'Innovation Labs',
      description:
        'Collaborate on cutting-edge healthcare technology initiatives',
      features: [
        'Proof of concept development',
        'Pilot program management',
        'Research partnerships',
        'Technology incubation',
      ],
    },
    {
      title: 'Managed Services',
      description: 'Ongoing support and optimization of healthcare systems',
      features: [
        '24/7 system monitoring',
        'Performance optimization',
        'Continuous improvement',
        'Staff augmentation',
      ],
    },
  ];

  return (
    <Box sx={{ mb: 8 }}>
      <Heading
        variant="h2"
        sx={{ mb: 4, fontSize: '1.75rem', fontWeight: 600 }}
      >
        Engagement Models
      </Heading>
      <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
        {collaborationModels.map((model, index) => (
          <Grid item xs={12} lg={6} key={index}>
            <GreenCornerCard
              index={index}
              maxWidth="450px"
              sx={{
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                '&::before': {
                  width: '50px',
                  height: '50px',
                },
                '&::after': {
                  width: '50px',
                  height: '50px',
                },
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                {/* Model Icon/Number */}
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor:
                      'color-mix(in srgb, var(--color-primary) 10%, transparent)',
                    border: '2px solid var(--color-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: 'primary.main',
                    mb: 3,
                  }}
                >
                  {String(index + 1).padStart(2, '0')}
                </Box>

                <Heading
                  variant="h5"
                  sx={{
                    mb: 3,
                    fontSize: '1.4rem',
                    color: 'primary.main',
                    fontWeight: 600,
                  }}
                >
                  {model.title}
                </Heading>
                <Text
                  sx={{
                    mb: 3,
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    color: 'text.primary',
                  }}
                >
                  {model.description}
                </Text>

                {/* Features with enhanced styling */}
                <Box
                  sx={{
                    backgroundColor:
                      'color-mix(in srgb, var(--color-primary) 2%, transparent)',
                    p: 2,
                    borderRadius: 1,
                    border:
                      '1px solid color-mix(in srgb, var(--color-primary) 10%, transparent)',
                  }}
                >
                  <Text
                    sx={{
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: 'primary.main',
                      mb: 2,
                    }}
                  >
                    Key Features:
                  </Text>
                  <List dense sx={{ '& .MuiListItem-root': { py: 0.5 } }}>
                    {model.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircleIcon
                            sx={{ color: 'primary.main', fontSize: 18 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            sx: {
                              fontSize: '0.95rem',
                              fontWeight: 500,
                              lineHeight: 1.4,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                {/* CTA Button */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={() =>
                      window.open(
                        'https://www.linkedin.com/company/rsm-collective/',
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                    aria-label="Contact RSM Collective on LinkedIn (opens in new window)"
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      px: 3,
                      py: 1,
                      fontSize: '0.9rem',
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: '8px',
                      borderBottomLeftRadius: '8px',
                      borderBottomRightRadius: 0,
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -2,
                        left: -2,
                        width: '12px',
                        height: '12px',
                        borderTop: '2px solid var(--color-primary)',
                        borderLeft: '2px solid var(--color-primary)',
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -2,
                        right: -2,
                        width: '12px',
                        height: '12px',
                        borderBottom: '2px solid var(--color-primary)',
                        borderRight: '2px solid var(--color-primary)',
                      },
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    }}
                  >
                    Learn More
                  </Button>
                </Box>
              </CardContent>
            </GreenCornerCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default EngagementModelsSection;
